/**
 * @file ClientSettingSupportedLanguages.js
 * @description This file contains the ClientSettingSupportedLanguages component, which is a reusable form for displaying and editing an client's supported languages information.
 */

import { useState, useEffect, useRef } from 'react';
import {
    getClientLocales,
    updateClientLocales,
} from '../../../services/clientService';
import { fetchLocales } from '../../../services/systemService';
import Toast from '../../ui/toasts/Toast';
import { SecurityAttributes } from '../../../utils/data/enums';
import { useAuthContext } from '../../../store/Contexts';
import { SearchableDropdown } from '../../helpers/SearchableDropdown';
import ClientOptionButtons from './ClientOptionButtons'
import { getText } from '../../../i18n';
import styles from './ClientSettingSupportedLanguages.module.scss';

const ClientSettingSupportedLanguages = () => {
    const didRun = useRef(false);

    const [
        initialSelectedLanguages,
        setInitialSelectedLanguages,
    ] = useState([]);

    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [languagesForDropdown, setLanguagesForDropDown] = useState([]);

    const { hasAttribute } = useAuthContext();

    const getOptions = async () => {
        try {
            const [allSystemLanguages, tenantLocales] = await Promise.all([fetchLocales(), getClientLocales()]);

            // Create the new array with the desired structure
            const systemLanguages = (allSystemLanguages ?? []).map(lang => ({
                name: lang.name,
                id: lang.id,
                isDefault: lang.isDefault
            }));

            setLanguagesForDropDown(
                systemLanguages.filter(lang => !lang.isDefault),
            );

            const selectedLocales = [];

            tenantLocales.map(tenantLocale => {
                var locale = systemLanguages.find(l => l.id === tenantLocale.localeId);

                if (locale && !locale.isDefault) {
                    selectedLocales.push({ name: locale.name, id: locale.id });
                }
            });

            setInitialSelectedLanguages(selectedLocales);

            if (tenantLocales) {
                setSelectedLanguages(selectedLocales);
            } else {
                Toast.error(
                    getText('msg_4ebc9f57122c'),
                );
            }

        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    // Function to update selected languages based on input array
    function updateSelectedLanguages(languagesToSelect) {
        setSelectedLanguages(languagesToSelect);
    }

    useEffect(() => {
        if (!didRun.current) {
            getOptions();
            didRun.current = true;
        }
    }, []);

    const handleCancel = async () => {
        setSelectedLanguages(
            initialSelectedLanguages,
        )
    }

    const handleSave = async () => {

        const initialLanguageIds = [...initialSelectedLanguages.map(l => l.id)];
        const selectedLanguageIds = [...selectedLanguages.map(l => l.id)];

        let hasChanges = false;

        // if there is a delete
        for (const id of initialLanguageIds) {
            if (!selectedLanguageIds.includes(id)) {
                hasChanges = true;
                break;
            }
        }

        // if there is an add
        if (!hasChanges) {
            for (const id of selectedLanguageIds) {
                if (!initialLanguageIds.includes(id)) {
                    hasChanges = true;
                    break;
                }
            }
        }

        if (!hasChanges) {
            Toast.warning(getText('msg_13c3a7ac4e47'));
            return;
        }

        // Prepare payload for API
        const updatedLocales = {
            locales: selectedLanguages.map(lang => ({
                LocaleId: lang.id
            })),
        };

        // Call API and handle response
        const response = await updateClientLocales(updatedLocales);

        if (response.error) {
            Toast.error(
                getText('msg_2d8999548c1b'),
            );
            return;
        }

        // Update initial state and notify success
        setInitialSelectedLanguages(selectedLanguages);
        Toast.success(getText('msg_308279915faa'));
    };

    return (
        <>
            <div style={{ margin: '36px 36px 0px 24px' }}>
                <p className={styles.heading}>
                    {getText('msg_cec3fafbcb16')}
                </p>
                <p className={styles.subtext}>
                    {getText('msg_7a9e634cd957')}
                </p>
                <div className={styles.dropdownWrapper}>
                    <SearchableDropdown
                        placeholderLabel={getText('msg_8598222918d3')}
                        options={languagesForDropdown}
                        optionIDField="id"
                        savedOptions={selectedLanguages}
                        savedOptionIDField="id"
                        updateOptions={selectedOptions =>
                            updateSelectedLanguages(selectedOptions)
                        }
                        disabled={false}
                        showTags={true}
                    />
                </div>
            </div>
            <div style={{ margin: '0px 0px 80px 0px' }}>
                {hasAttribute(
                    SecurityAttributes.ClientSettingsFullAccess
                ) && (<ClientOptionButtons handleSave={handleSave} handleCancel={handleCancel} />)}
            </div>
        </>
    );
};

export default ClientSettingSupportedLanguages;
